import type { EntryKey } from "@pinia/colada";
import type { MaybeRefOrGetter } from "vue";
import type {
  FileUploadForm,
  KnowledgeQueryResult,
  KnowledgeSchemaType,
} from "@/schemas/knowledge.schema";
import { useMutation, useQuery, useQueryCache } from "@pinia/colada";
import { toValue } from "vue";
import { knowledgeApi } from "@/api/knowledge.api";
import { SUBSCRIPTION_LIMITS_QUERY_KEYS } from "@/queries/subscription-limits.query";
import { useProjectStore } from "@/stores/project.store";

// ==================== QUERY KEYS ====================

export const KNOWLEDGE_QUERY_KEYS = {
  root: ["knowledge"] as const,
  list: (projectId?: string | null) =>
    [...KNOWLEDGE_QUERY_KEYS.root, "list", { projectId }] as const,
  collection: (id: string, projectId?: string | null) =>
    [...KNOWLEDGE_QUERY_KEYS.root, id, { projectId }] as const,
};

// ==================== KNOWLEDGE COLLECTIONS LIST ====================

/**
 * Query for knowledge collections list (without documents)
 *
 * Features:
 * - **Project filtering**: Automatically filters by selected project via X-Project-ID header
 * - Query refetches when selected project changes
 */
export function useKnowledgeCollectionsQuery() {
  const projectStore = useProjectStore();

  return useQuery({
    // Include project ID in key so query refetches when project changes
    key: () => KNOWLEDGE_QUERY_KEYS.list(projectStore.currentProjectId) as unknown as EntryKey,
    query: async () => {
      // Pass project header to filter by selected project
      return await knowledgeApi.get({
        headers: projectStore.getProjectHeader(),
      });
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

// ==================== KNOWLEDGE COLLECTION DETAIL ====================

/**
 * Query for single knowledge collection with documents
 * Used in detail/edit view (Knowledge.tsx)
 */
export function useKnowledgeCollectionQuery(id: MaybeRefOrGetter<string>) {
  const projectStore = useProjectStore();

  return useQuery({
    key: () =>
      KNOWLEDGE_QUERY_KEYS.collection(
        toValue(id),
        projectStore.currentProjectId
      ) as unknown as EntryKey,
    query: async () => {
      return await knowledgeApi.getCollection(toValue(id), {
        headers: projectStore.getProjectHeader(),
      });
    },
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

// ==================== KNOWLEDGE CRUD MUTATIONS ====================

/**
 * Create new knowledge collection
 *
 * The collection is created in the currently selected project.
 * If "All Projects" is selected, it creates an org-level collection.
 */
export function useCreateKnowledge() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: {
      name: string;
      description: string;
      model_name: string;
    }) => {
      return await knowledgeApi.createCollection(
        payload.name,
        {
          description: payload.description,
          model_name: payload.model_name,
        },
        {
          headers: projectStore.getProjectHeader(),
        }
      );
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: KNOWLEDGE_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Update knowledge collection metadata
 */
export function useUpdateKnowledge() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: {
      id: string;
      description?: string;
      model_name?: string;
    }) => {
      const { id, ...body } = payload;
      return await knowledgeApi.updateCollection(id, body as any, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: KNOWLEDGE_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Delete knowledge collection
 */
export function useDeleteKnowledge() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (id: string) => {
      return await knowledgeApi.removeCollection(id, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: KNOWLEDGE_QUERY_KEYS.root as any });
      queryCache.invalidateQueries({ key: SUBSCRIPTION_LIMITS_QUERY_KEYS.root as any });
    },
  });
}

// ==================== DOCUMENT OPERATIONS ====================

/**
 * Add documents to existing collection
 */
export function useAddToKnowledge() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: { id: string; documents: Record<string, any>[] }) => {
      return await knowledgeApi.addToCollection(payload.id, payload.documents, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: (_data, variables) => {
      queryCache.invalidateQueries({
        key: KNOWLEDGE_QUERY_KEYS.collection(variables.id) as any,
      });
      queryCache.invalidateQueries({ key: KNOWLEDGE_QUERY_KEYS.root as any });
    },
  });
}

/**
 * Sync knowledge collection documents
 * Used for updating documents and schema in detail view
 */
export function useSyncKnowledge() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: {
      id: string;
      documents: any[];
      model?: string;
      schema?: KnowledgeSchemaType;
    }) => {
      const { id, ...body } = payload;
      return await knowledgeApi.syncKnowledge(id, body, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: (_data, variables) => {
      queryCache.invalidateQueries({
        key: KNOWLEDGE_QUERY_KEYS.collection(variables.id) as any,
      });
      queryCache.invalidateQueries({ key: KNOWLEDGE_QUERY_KEYS.root as any });
    },
  });
}

// ==================== FILE OPERATIONS ====================

/**
 * Upload file to create or add to knowledge collection
 */
export function useUploadKnowledgeFile() {
  const queryCache = useQueryCache();
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: {
      id: string;
      file: File;
      model?: string;
      options: FileUploadForm["options"];
    }) => {
      return await knowledgeApi.uploadFile(payload, {
        headers: projectStore.getProjectHeader(),
      });
    },
    onSuccess: () => {
      queryCache.invalidateQueries({ key: KNOWLEDGE_QUERY_KEYS.root as any });
    },
  });
}

// ==================== QUERY OPERATIONS ====================

/**
 * Query knowledge collection (RAG search)
 */
export function useQueryKnowledge() {
  const projectStore = useProjectStore();

  return useMutation({
    mutation: async (payload: {
      id: string;
      query: string;
      n?: number;
    }): Promise<KnowledgeQueryResult[]> => {
      const { id, ...body } = payload;
      return await knowledgeApi.query(id, body, {
        headers: projectStore.getProjectHeader(),
      });
    },
  });
}
